<template>
	<q-dialog v-model="showDialog">
		<q-card class="share-card">
			<q-card-section>
				<div class="text-h6" style="color: #ff6600">Share Character</div>
				<div class="text-caption" style="color: #888">{{ characterName }}</div>
			</q-card-section>

			<q-card-section>
				<!-- Existing shares (from character's sharedWith array) -->
				<div v-if="activeShareAliases.length > 0" class="q-mb-md">
					<div class="text-caption q-mb-xs" style="color: #aaa">Shared with:</div>
					<div class="row q-gutter-sm">
						<q-chip
							v-for="alias in activeShareAliases"
							:key="alias"
							removable
							@remove="revokeShare(alias)"
							color="green-8"
							text-color="white"
							icon="person"
						>
							{{ alias }}
						</q-chip>
					</div>
				</div>

				<!-- Pending shares -->
				<div v-if="pendingShares.length > 0" class="q-mb-md">
					<div class="text-caption q-mb-xs" style="color: #aaa">Pending:</div>
					<div class="row q-gutter-sm">
						<q-chip
							v-for="share in pendingShares"
							:key="share.id"
							removable
							@remove="cancelShare(share.id)"
							color="orange-8"
							text-color="white"
							icon="hourglass_empty"
						>
							{{ share.toAlias }}
						</q-chip>
					</div>
				</div>

				<!-- Add new share -->
				<div class="row q-gutter-sm items-end">
					<q-input
						v-model="aliasInput"
						dark
						filled
						dense
						label="Enter alias"
						class="col"
						@keyup.enter="sendShare"
						:loading="searching"
					>
						<template v-slot:append v-if="aliasInput.length >= 2">
							<q-menu v-model="showSuggestions" no-parent-event auto-close>
								<q-list dark style="background: #1a1a2e; min-width: 200px">
									<q-item
										v-for="s in suggestions"
										:key="s.alias"
										clickable
										@click="selectSuggestion(s.alias)"
									>
										<q-item-section avatar>
											<q-icon name="person" :color="s.isFriend ? 'green' : 'grey'" />
										</q-item-section>
										<q-item-section>
											<q-item-label>{{ s.alias }}</q-item-label>
											<q-item-label caption>{{ s.tags.join(', ') }}</q-item-label>
										</q-item-section>
										<q-item-section side v-if="s.isFriend">
											<q-icon name="star" color="yellow" size="xs" />
										</q-item-section>
									</q-item>
									<q-item v-if="suggestions.length === 0">
										<q-item-section>
											<q-item-label caption>No matches</q-item-label>
										</q-item-section>
									</q-item>
								</q-list>
							</q-menu>
						</template>
					</q-input>
					<q-btn
						color="green"
						icon="share"
						label="Share"
						dense
						:disable="!aliasInput.trim()"
						@click="sendShare"
					/>
				</div>
				<div v-if="shareError" class="text-negative text-caption q-mt-xs">{{ shareError }}</div>
			</q-card-section>

			<q-card-actions align="right">
				<q-btn flat label="Close" color="grey" @click="showDialog = false" />
			</q-card-actions>
		</q-card>
	</q-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useInviteStore } from '../stores/invite';
import { useCharacterStore } from '../stores/character';
import { useUserStore } from '../stores/user';
import { Firestore, collection, getDocs } from 'firebase/firestore';

const props = defineProps<{
	modelValue: boolean;
	characterId: string;
	characterName: string;
}>();

const emit = defineEmits<{
	(e: 'update:modelValue', value: boolean): void;
}>();

const inviteStore = useInviteStore();
const characterStore = useCharacterStore();
const userStore = useUserStore();

const aliasInput = ref('');
const shareError = ref('');
const searching = ref(false);
const showSuggestions = ref(false);
const suggestions = ref<{ alias: string; tags: string[]; isFriend: boolean }[]>([]);
const friendAliases = ref<string[]>([]);

const showDialog = computed({
	get: () => props.modelValue,
	set: (val) => emit('update:modelValue', val),
});

// Active shares from character's sharedWith array (aliases, maintained by Cloud Functions)
const activeShareAliases = computed(() => {
	const char = characterStore.characterById(props.characterId);
	return (char as any)?.sharedWith || [];
});

// Pending shares from sent invites
const pendingShares = computed(() =>
	inviteStore.sent.filter(i =>
		i.type === 'character-share' &&
		i.characterId === props.characterId
	)
);

watch(aliasInput, async (val) => {
	if (val.length < 2) {
		suggestions.value = [];
		showSuggestions.value = false;
		return;
	}
	searching.value = true;
	const results = await inviteStore.searchAliases(val);
	suggestions.value = results.map(r => ({
		...r,
		isFriend: friendAliases.value.includes(r.alias),
	})).sort((a, b) => {
		if (a.isFriend && !b.isFriend) return -1;
		if (!a.isFriend && b.isFriend) return 1;
		return 0;
	});
	showSuggestions.value = suggestions.value.length > 0;
	searching.value = false;
});

const selectSuggestion = (alias: string) => {
	aliasInput.value = alias;
	showSuggestions.value = false;
};

const sendShare = async () => {
	shareError.value = '';
	const alias = aliasInput.value.trim();
	if (!alias) return;

	if (alias === userStore.alias) {
		shareError.value = "You can't share with yourself";
		return;
	}

	// Check if already shared
	if (activeShareAliases.value.includes(alias)) {
		shareError.value = 'Already shared with this alias';
		return;
	}
	if (pendingShares.value.some(s => s.toAlias === alias)) {
		shareError.value = 'Share invite already pending';
		return;
	}

	try {
		await inviteStore.sendCharacterShare(alias, props.characterId);
		aliasInput.value = '';
	} catch (error: any) {
		shareError.value = error?.message || 'Failed to send share';
	}
};

const revokeShare = async (alias: string) => {
	await inviteStore.revokeCharacterShare(props.characterId, alias);
	// Refresh character data to update sharedWith
	await characterStore.fetchAll();
};

const cancelShare = async (inviteId: string) => {
	await inviteStore.cancelInvite(inviteId);
};

onMounted(async () => {
	await inviteStore.fetchSent();

	// Load friends list from Firestore
	if (userStore.id) {
		const { $firestore } = useNuxtApp();
		const snap = await getDocs(
			collection($firestore as Firestore, `friends/${userStore.id}/friends`)
		);
		friendAliases.value = snap.docs.map(d => d.data().alias as string);
	}
});
</script>

<style scoped>
.share-card {
	background: linear-gradient(135deg, #1c1c1c, #000000);
	color: #00ffea;
	border: 1px solid #00ffea;
	box-shadow: 0 0 8px 3px #00ffea40;
	border-radius: 12px;
	min-width: 400px;
}
</style>
